import { calculateCanvasSize } from "./calculateCanvasSize";

const cloneTemplate = (template = {}) => {
  const source = template || {};
  return {
    ...source,
    objects: (source.objects || []).map((object) => ({ ...object })),
    fields: (source.fields || []).map((field) => ({ ...field })),
  };
};

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

export const calculateTemplateForSize = (originalTemplate, baseSize, targetSize) => {
  if (!originalTemplate || !baseSize || !targetSize) {
    return cloneTemplate(originalTemplate);
  }

  const canvasSize = calculateCanvasSize(baseSize);
  const targetCanvasSize = calculateCanvasSize(targetSize);
  const originalHeight = canvasSize.height;
  const originalWidth = canvasSize.width;

  if (
    originalHeight === targetCanvasSize.height &&
    originalWidth === targetCanvasSize.width
  ) {
    return cloneTemplate(originalTemplate);
  }

  const originalDiagonal = Math.sqrt(originalWidth ** 2 + originalHeight ** 2);
  const targetDiagonal = Math.sqrt(
    targetCanvasSize.width ** 2 + targetCanvasSize.height ** 2
  );
  const diagonalRatio = targetDiagonal / originalDiagonal;

  const convert = (value, isWidth) => {
    if (!isNumber(value)) {
      return value;
    }

    const originalDimension = isWidth ? originalWidth : originalHeight;
    const newDimension = isWidth
      ? targetCanvasSize.width
      : targetCanvasSize.height;
    return value * (newDimension / originalDimension);
  };

  const hasBox = (element) =>
    isNumber(element?.top) &&
    isNumber(element?.left) &&
    isNumber(element?.width) &&
    isNumber(element?.height);

  const isElementInsideOfCanvas = (element) =>
    hasBox(element) &&
    element.top > 0 &&
    element.top + element.height < originalHeight;

  const isElementInTheTopPartVertically = (element) =>
    isNumber(element?.top) && element.top <= Math.floor(originalHeight / 2);

  const allElements = [
    ...(originalTemplate.objects || []),
    ...(originalTemplate.fields || []),
  ].sort((a, b) => (a.top ?? 0) - (b.top ?? 0));

  const processedTopPositions = new Map();

  allElements.forEach((element, index) => {
    const prevElement = index > 0 ? allElements[index - 1] : null;

    const isInTheSamePartVertically =
      prevElement &&
      isElementInsideOfCanvas(element) &&
      isElementInsideOfCanvas(prevElement) &&
      isElementInTheTopPartVertically(element) ===
      isElementInTheTopPartVertically(prevElement);

    if (isInTheSamePartVertically) {
      const originalGap = element.top - prevElement.top;
      const prevProcessedTop = processedTopPositions.get(prevElement);
      processedTopPositions.set(element, prevProcessedTop + originalGap);
    } else {
      processedTopPositions.set(element, convert(element.top, false));
    }
  });

  const isElementInside = (elem1, elem2) =>
    hasBox(elem1) &&
    hasBox(elem2) &&
    elem1.top >= elem2.top &&
    elem1.top + elem1.height <= elem2.top + elem2.height &&
    elem1.left >= elem2.left &&
    elem1.left + elem1.width <= elem2.left + elem2.width;

  const getContainmentOffsets = (innerElem, outerElem) => ({
    topOffset: innerElem.top - outerElem.top,
    bottomOffset:
      outerElem.top + outerElem.height - (innerElem.top + innerElem.height),
    leftOffset: innerElem.left - outerElem.left,
    rightOffset:
      outerElem.left + outerElem.width - (innerElem.left + innerElem.width),
  });

  const containments = new Map();

  allElements.forEach((elem1) => {
    allElements.forEach((elem2) => {
      if (elem1 !== elem2 && isElementInside(elem1, elem2)) {
        if (!containments.has(elem1)) {
          containments.set(elem1, {
            container: elem2,
            offsets: getContainmentOffsets(elem1, elem2),
          });
        }
      }
    });
  });

  const convertElement = (element) => {
    const { left, top, width, height, fontSize, ...rest } = element;

    const converted = {
      left: convert(left, true),
      top: processedTopPositions.get(element),
      width: convert(width, true),
      height: convert(height, false),
      ...(fontSize && {
        fontSize: Math.round(fontSize * diagonalRatio),
      }),
      ...rest,
    };

    if (containments.has(element)) {
      const { container, offsets } = containments.get(element);
      const newContainer = {
        top: convert(container.top, false),
        left: convert(container.left, true),
        width: convert(container.width, true),
        height: convert(container.height, false),
      };
      const scaleY = targetCanvasSize.height / canvasSize.height;
      const scaleX = targetCanvasSize.width / canvasSize.width;
      const scaledTopOffset = offsets.topOffset * scaleY;
      const scaledLeftOffset = offsets.leftOffset * scaleX;

      converted.top = newContainer.top + scaledTopOffset;
      converted.left = newContainer.left + scaledLeftOffset;

      const maxTop = newContainer.top + newContainer.height - converted.height;
      const maxLeft = newContainer.left + newContainer.width - converted.width;

      converted.top = Math.min(
        maxTop,
        Math.max(newContainer.top, converted.top)
      );
      converted.left = Math.min(
        maxLeft,
        Math.max(newContainer.left, converted.left)
      );
    }

    return converted;
  };

  return {
    ...originalTemplate,
    objects: (originalTemplate.objects || []).map(convertElement),
    fields: (originalTemplate.fields || []).map(convertElement),
  };
};
